/**
 * @fileoverview API client for the Address Extension backend module.
 *
 * Provides typed methods for all REST API endpoints of the Arad.AddressExtension backend module.
 *
 * IMPORTANT: address.key == address.id for loaded entities!
 * VirtoCommerce sets key = id when loading addresses from database.
 * The database has NO key column - key is a runtime property only.
 * You can use either address.id or address.key when calling these methods.
 */

import { useFetch } from "@/core/api/common";
import { AddressSource, PrivateAssetType } from "../types";
import type {
  IAddressExtension,
  IAddressExtensionInput,
  IAddressExtensionSearchCriteria,
  IAddressExtensionSearchResult,
  IAddressExtensionValidationContext,
  IAddressExtensionValidationResult,
  IGuildApiResponse,
  IGuildOption,
  IPrivateAssetResponse,
  IPrivateAssetUploadResult,
} from "../types";

const API_BASE = "/api/arad";
const STOREFRONT_API_BASE = "/api/arad/storefront";

/**
 * Search for address extensions with optional filtering.
 * NOTE: This is an ADMIN endpoint - requires permissions.
 */
export async function searchAddressExtensions(
  criteria: IAddressExtensionSearchCriteria,
): Promise<IAddressExtensionSearchResult> {
  const { data, error } = await useFetch(`${API_BASE}/address-extensions/search`)
    .post(criteria)
    .json<IAddressExtensionSearchResult>();

  if (error.value) {
    throw new Error(`Failed to search address extensions: ${error.value}`);
  }

  return data.value ?? { totalCount: 0, results: [] };
}

/**
 * Get an address extension by its ID.
 * NOTE: This is an ADMIN endpoint - requires permissions.
 */
export async function getAddressExtensionById(id: string): Promise<IAddressExtension | null> {
  const { data, error } = await useFetch(
    `${API_BASE}/address-extensions/${encodeURIComponent(id)}`,
  ).json<IAddressExtension>();

  if (error.value) {
    // Return null for 404, throw for other errors
    if (error.value.toString().includes("404")) {
      return null;
    }
    throw new Error(`Failed to get address extension: ${error.value}`);
  }

  return data.value ?? null;
}

/**
 * Get an address extension by the linked address ID and source.
 * Uses STOREFRONT API with ownership validation.
 * Uses plain fetch to avoid reactive watchers causing memory issues.
 */
export async function getAddressExtensionByAddressId(
  addressId: string,
  addressSource: AddressSource,
): Promise<IAddressExtension | null> {
  const url = `${STOREFRONT_API_BASE}/address-extensions/by-address/${encodeURIComponent(addressId)}?addressSource=${addressSource}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 404) {
        console.log(`[AddressExtension API] No extension found for address ${addressId}`);
        return null;
      }
      if (response.status === 403) {
        console.error(`[AddressExtension API] Access denied when fetching extension for address ${addressId}`);
        throw new Error(`Access denied: You don't have permission to access this address extension`);
      }
      throw new Error(`Failed to get address extension: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`[AddressExtension API] Successfully loaded extension for address ${addressId}:`, data);
    return data;
  } catch (error) {
    console.error(`[AddressExtension API] Error fetching extension for address ${addressId}:`, error);
    // Re-throw to let caller handle the error
    throw error;
  }
}

/**
 * Create or update an address extension.
 * Uses STOREFRONT API with ownership validation.
 */
export async function saveAddressExtension(input: IAddressExtensionInput): Promise<IAddressExtension> {
  const { data, error } = await useFetch(`${STOREFRONT_API_BASE}/address-extensions/update`)
    .post(input)
    .json<IAddressExtension>();

  if (error.value) {
    throw new Error(`Failed to save address extension: ${error.value}`);
  }

  if (!data.value) {
    throw new Error("Failed to save address extension: No data returned");
  }

  return data.value;
}

/**
 * Validate an address extension for a specific context (shipping/billing).
 * Uses STOREFRONT API with ownership validation.
 */
export async function validateAddressExtension(
  context: IAddressExtensionValidationContext,
): Promise<IAddressExtensionValidationResult> {
  const { data, error } = await useFetch(`${STOREFRONT_API_BASE}/address-extensions/validate`)
    .post(context)
    .json<IAddressExtensionValidationResult>();

  if (error.value) {
    throw new Error(`Failed to validate address extension: ${error.value}`);
  }

  return data.value ?? { isValid: false, errors: ["Unknown validation error"] };
}

/**
 * Delete an address extension by address ID and source.
 * Uses STOREFRONT API with ownership validation.
 * @param addressId The address ID to delete extension for
 * @param addressSource The source of the address (default: Member)
 */
export async function deleteAddressExtension(
  addressId: string,
  addressSource: AddressSource = AddressSource.Member,
): Promise<void> {
  const { error } = await useFetch(
    `${STOREFRONT_API_BASE}/address-extensions/${encodeURIComponent(addressId)}?addressSource=${addressSource}`,
  ).delete();

  if (error.value) {
    throw new Error(`Failed to delete address extension: ${error.value}`);
  }
}

/**
 * Upload a private asset (national card or business license).
 * Uses owner-based validation (user can only upload their own assets).
 */
export async function uploadPrivateAsset(file: File, assetType: PrivateAssetType): Promise<IPrivateAssetUploadResult> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("assetType", assetType);

  const { data, error } = await useFetch(`${API_BASE}/private-assets/upload`)
    .post(formData)
    .json<IPrivateAssetResponse>();

  if (error.value) {
    return {
      asset: {} as IPrivateAssetResponse,
      success: false,
      error: `Failed to upload asset: ${error.value}`,
    };
  }

  // Backend returns the asset directly, wrap it in the result structure
  if (data.value && data.value.id) {
    return {
      asset: data.value,
      success: true,
    };
  }

  return { asset: {} as IPrivateAssetResponse, success: false, error: "No response data" };
}

/**
 * Get a private asset with a signed URL for viewing.
 * Uses owner-based validation (user can only get their own assets).
 */
export async function getPrivateAsset(id: string): Promise<IPrivateAssetResponse | null> {
  const { data, error } = await useFetch(
    `${API_BASE}/private-assets/${encodeURIComponent(id)}`,
  ).json<IPrivateAssetResponse>();

  if (error.value) {
    if (error.value.toString().includes("404")) {
      return null;
    }
    throw new Error(`Failed to get private asset: ${error.value}`);
  }

  return data.value ?? null;
}

/**
 * Delete a private asset.
 * Uses owner-based validation (user can only delete their own assets).
 */
export async function deletePrivateAsset(id: string): Promise<void> {
  const { error } = await useFetch(`${API_BASE}/private-assets/${encodeURIComponent(id)}`).delete();

  if (error.value) {
    throw new Error(`Failed to delete private asset: ${error.value}`);
  }
}

/**
 * Get the list of available guilds.
 * @param cultureName - The culture name for localized results (e.g., 'fa-IR', 'en-US')
 */
export async function getGuilds(cultureName?: string): Promise<IGuildOption[]> {
  const url = cultureName ? `${API_BASE}/guilds?cultureName=${encodeURIComponent(cultureName)}` : `${API_BASE}/guilds`;
  const { data, error } = await useFetch(url).json<IGuildApiResponse[]>();

  if (error.value) {
    throw new Error(`Failed to get guilds: ${error.value}`);
  }

  // Transform API response {id, name} to frontend format {value, label}
  const apiData = data.value ?? [];
  return apiData.map((item) => ({
    value: item.id,
    label: item.name,
  }));
}

// Re-export types that may be needed by consumers
export { AddressSource, PrivateAssetType };
